import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, ArrowLeft } from 'lucide-react';
import { returnsApi } from '../api/returns';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';

const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

const PurchaseReturns: React.FC = () => {
  const { showToast } = useToast();
  const [returns, setReturns]   = useState<any[]>([]);
  const [total, setTotal]       = useState(0);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [grns, setGrns]         = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedGrn, setSelectedGrn] = useState('');
  const [grnData, setGrnData]   = useState<any>(null);
  const [returnLines, setReturnLines] = useState<any[]>([]);
  const [form, setForm] = useState({ branch_id:'', return_date: new Date().toISOString().split('T')[0], reason:'' });

  const load = useCallback(() => {
    setLoading(true);
    returnsApi.list({ search }).then(res => { setReturns(res.data.data); setTotal(res.data.total); setLoading(false); });
  }, [search]);

  useEffect(() => {
    purchaseApi.grns({ search:'' }).then(r => setGrns(r.data.data.filter((g:any) => g.status === 'Received')));
    purchaseApi.branches().then(r => setBranches(r.data.data));
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const handleGrnSelect = async (grnId: string) => {
    setSelectedGrn(grnId);
    setGrnData(null);
    setReturnLines([]);
    if (!grnId) return;
    const res = await returnsApi.getGrnItems(Number(grnId));
    setGrnData(res.data.data);
    setReturnLines(res.data.data.items.map((item: any) => ({
      grn_item_id: item.id,
      product_id:  item.product_id,
      location_id: item.location_id,
      product_name: item.product_name,
      sku:         item.sku,
      unit_symbol: item.unit_symbol,
      location_name: item.location_name,
      qty_received: item.qty_received,
      qty_available: item.qty_available_to_return,
      unit_cost:   item.unit_cost,
      qty_return:  0,
      include:     false,
    })));
  };

  const updateLine = (i: number, field: string, val: any) => {
    setReturnLines(l => l.map((line, idx) => idx===i ? {...line,[field]:val} : line));
  };

  const handleSubmit = async () => {
    const items = returnLines.filter(l => l.include && l.qty_return > 0);
    if (!items.length) { showToast('Select at least one item to return', 'error'); return; }
    if (!form.branch_id) { showToast('Branch required', 'error'); return; }
    setSaving(true);
    try {
      const res = await returnsApi.createReturn({
        grn_id:     Number(selectedGrn),
        contact_id: grnData.grn.contact_id,
        branch_id:  Number(form.branch_id),
        return_date: form.return_date,
        reason:     form.reason,
        items:      items.map(l => ({ grn_item_id:l.grn_item_id, product_id:l.product_id, location_id:l.location_id, qty_return:Number(l.qty_return), unit_cost:Number(l.unit_cost) })),
      });
      showToast(res.data.message);
      setShowForm(false); setSelectedGrn(''); setGrnData(null); setReturnLines([]);
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const grandTotal = returnLines.filter(l => l.include).reduce((s,l) => s + (Number(l.qty_return)*Number(l.unit_cost)), 0);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Purchase Returns</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13}/> New Return</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Purchase Return</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">GRN *</label>
                <select className="form-control" value={selectedGrn} onChange={e => handleGrnSelect(e.target.value)}>
                  <option value="">Select GRN</option>
                  {grns.map(g => <option key={g.id} value={g.id}>{g.grn_no} — {g.supplier_name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => setForm(f=>({...f,branch_id:e.target.value}))}>
                  <option value="">Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Return Date *</label>
                <input type="date" className="form-control" value={form.return_date} onChange={e => setForm(f=>({...f,return_date:e.target.value}))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom:14 }}>
              <label className="form-label">Reason</label>
              <input className="form-control" value={form.reason} onChange={e => setForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Damaged goods, wrong items..." />
            </div>

            {returnLines.length > 0 && (
              <>
                <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:'var(--tx2)' }}>Select items to return:</div>
                <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
                  <thead><tr style={{ background:'var(--sur2)' }}>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>✓</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Received</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Available</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Return Qty</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                  </tr></thead>
                  <tbody>
                    {returnLines.map((line, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid var(--bdr)', opacity: line.qty_available <= 0 ? 0.4 : 1 }}>
                        <td style={{ padding:'6px 10px' }}>
                          <input type="checkbox" checked={line.include} disabled={line.qty_available <= 0}
                            onChange={e => updateLine(i,'include',e.target.checked)} />
                        </td>
                        <td style={{ padding:'6px 10px', fontFamily:'monospace', fontSize:12, color:'var(--blue)' }}>{line.sku}</td>
                        <td style={{ padding:'6px 10px', fontSize:12 }}>{line.product_name}</td>
                        <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx3)' }}>{line.location_name}</td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{line.qty_received}</td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontSize:12, color:'var(--green)' }}>{line.qty_available}</td>
                        <td style={{ padding:'6px 4px', width:90 }}>
                          <input type="number" className="form-control mono" value={line.qty_return}
                            max={line.qty_available} min={0} disabled={!line.include}
                            onChange={e => updateLine(i,'qty_return',Math.min(Number(e.target.value), line.qty_available))}
                            style={{ textAlign:'right' }} />
                        </td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{Number(line.unit_cost).toFixed(2)}</td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontWeight:600, fontSize:12, color:'var(--red)' }}>
                          {(Number(line.qty_return)*Number(line.unit_cost)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:14, color:'var(--red)' }}>Return Total: {grandTotal.toFixed(2)}</div>
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Posting...' : 'Post Return'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)"/><input placeholder="Search returns..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} records</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Return No</th><th>Supplier</th><th style={{textAlign:'right'}}>Amount</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            : returns.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No purchase returns yet.</p></div></td></tr>
            : returns.map(r => (
              <tr key={r.id}>
                <td className="td-code">{r.return_no}</td>
                <td style={{fontWeight:600}}>{r.supplier_name}</td>
                <td className="td-num td-red">{Number(r.total_amount).toFixed(2)}</td>
                <td><span className={`badge ${colorMap[r.status_color]||'badge-gray'}`}>{r.status}</span></td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PurchaseReturns;
